import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { MotionButton } from '@iheart/ui';
import { api } from '../lib/api';

interface ProfileMfa {
  mfaEnabled: boolean;
  mfaRequirement?: {
    required: boolean;
    daysLeft: number | null;
    enforced: boolean;
  };
}

/**
 * Enforces MFA for paid tiers:
 *  - During the grace window → a slim countdown banner nudging setup.
 *  - After the deadline (enforced) → a full-screen block until 2FA is enabled,
 *    always allowing Settings → Personal (where setup lives) + logout.
 * Free-trial / comped / internal users are never `required`, so this is inert.
 */
export function MfaRequiredGate() {
  const loc = useLocation();
  const prof = useQuery({
    queryKey: ['profile'],
    queryFn: () => api<ProfileMfa>('/me/profile'),
    staleTime: 60_000,
  });

  const req = prof.data?.mfaRequirement;
  const enabled = prof.data?.mfaEnabled;
  if (!req?.required || enabled) return null;

  const onSetupPage = loc.pathname.startsWith('/settings/personal');

  // Hard block once the grace period has passed.
  if (req.enforced && !onSetupPage) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-white/90 p-6 backdrop-blur-xl">
        <div className="max-w-md rounded-2xl border border-white/40 bg-white p-8 text-center shadow-2xl">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-amber-100">
            <ShieldAlert className="size-6 text-amber-600" />
          </div>
          <h2 className="text-lg font-bold text-neutral-800">Set up two-factor authentication</h2>
          <p className="mt-2 text-sm leading-relaxed text-neutral-500">
            Your plan requires 2FA for security, and the setup window has passed. Enable it now to
            continue — it takes about two minutes with an authenticator app.
          </p>
          <Link to="/settings/personal" className="mt-6 block">
            <MotionButton size="lg" className="w-full">
              Set up 2FA now
            </MotionButton>
          </Link>
        </div>
      </div>
    );
  }

  // Grace-period countdown banner.
  if (!req.enforced && req.daysLeft != null && !onSetupPage) {
    const days = Math.max(0, req.daysLeft);
    return (
      <div className="fixed inset-x-0 top-0 z-[150] flex items-center justify-center gap-3 bg-gradient-to-r from-pink-500 to-indigo-500 px-4 py-2 text-center text-xs font-medium text-white">
        <ShieldAlert className="size-4 shrink-0" />
        <span>
          Two-factor authentication is required on your plan — <strong>{days} day{days === 1 ? '' : 's'} left</strong> to set it up.
        </span>
        <Link
          to="/settings/personal"
          className="shrink-0 rounded-full bg-white/20 px-3 py-0.5 font-semibold hover:bg-white/30"
        >
          Set up now
        </Link>
      </div>
    );
  }

  return null;
}
